/* Resolve product illustrations in Journal against current product media.
 * Pixify replaces file URLs; article HTML can retain obsolete static URLs.
 * No article content is persisted or modified in the Shopify admin.
 */
(() => {
  if (window.cgJournalThumbnails) return;
  const products = new Map();
  const pending = new WeakSet();
  const normalize = (value) => (value || '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

  function productPath(link) {
    if (!link) return null;
    try {
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return null;
      const match = url.pathname.match(/^(.*?\/)(?:collections\/[^/]+\/)?products\/([a-z0-9-]+)\/?$/i);
      return match ? `${match[1]}products/${match[2]}` : null;
    } catch (_) { return null; }
  }

  function currentImage(path) {
    if (!products.has(path)) {
      const request = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const options = { signal: controller.signal, headers: { Accept: 'application/json' } };
          let response = await fetch(`${path}.js`, options);
          // Shopify redirects old product page handles, but not their .js endpoints.
          if (response.status === 404) {
            const page = await fetch(path, { signal: controller.signal });
            const redirectedPath = page.ok && productPath({ href: page.url });
            if (redirectedPath && redirectedPath !== path) response = await fetch(`${redirectedPath}.js`, options);
          }
          if (!response.ok) throw new Error('Product unavailable');
          const product = await response.json();
          // Same Pixify convention as catalogue cards: image 2 is the clean thumbnail.
          const candidate = product.images?.[1];
          const candidateSrc = typeof candidate === 'string' ? candidate : candidate?.src;
          const details = product.media?.find((media) => media.preview_image?.src?.split('?')[0].replace(/^https?:/, '') === candidateSrc?.split('?')[0].replace(/^https?:/, ''))?.preview_image;
          const first = product.images?.[0];
          const src = candidateSrc && (!details || details.width >= 500)
            ? candidateSrc : (typeof first === 'string' ? first : first?.src);
          if (!src) throw new Error('No product image');
          const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.href);
          if (url.protocol !== 'https:') throw new Error('Invalid image URL');
          url.searchParams.set('width', '600');
          return url.href;
        } finally { clearTimeout(timeout); }
      })();
      products.set(path, request);
      request.catch(() => products.delete(path));
    }
    return products.get(path);
  }

  function captionPath(image, root) {
    const figure = image.closest('figure.cg-journal-media');
    const figureCaption = figure?.querySelector('figcaption');
    if (figureCaption && root.contains(figure)) {
      const links = [...figureCaption.querySelectorAll('a[href]')].filter(link => normalize(link.textContent));
      if (links.length === 1 && normalize(figureCaption.textContent) === normalize(links[0].textContent)) return productPath(links[0]);
    }
    const block = image.parentElement;
    const caption = block?.nextElementSibling;
    if (!caption || !root.contains(caption) || block.querySelectorAll('img').length !== 1 || normalize(block.textContent)) return null;
    if (caption.querySelector('img') || normalize(caption.textContent) !== normalize(image.alt)) return null;
    const links = [...caption.querySelectorAll('a[href]')].filter(link => normalize(link.textContent));
    // An immediately adjacent, image-free caption can disambiguate duplicate alt text.
    return links.length === 1 && normalize(links[0].textContent) === normalize(image.alt) ? productPath(links[0]) : null;
  }

  async function replaceImage(image, path) {
    const src = await currentImage(path);
    // Do not discard the original until the replacement has actually loaded.
    await new Promise((resolve, reject) => {
      const probe = new Image();
      const timeout = setTimeout(() => { probe.onload = probe.onerror = null; reject(new Error('Image timeout')); }, 12000);
      probe.onload = () => { clearTimeout(timeout); resolve(); };
      probe.onerror = () => { clearTimeout(timeout); reject(new Error('Image unavailable')); };
      probe.src = src;
    });
    if (!image.isConnected) return;
    image.closest('picture')?.querySelectorAll('source').forEach((source) => source.removeAttribute('srcset'));
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
    image.dataset.journalProduct = path;
  }

  async function init(scope = document) {
    const jobs = [];
    const roots = [...scope.querySelectorAll('[data-journal-product-images]')];
    if (scope.matches?.('[data-journal-product-images]')) roots.unshift(scope);
    for (const root of roots) {
      const names = new Map();
      const handles = new Map();
      for (const link of root.querySelectorAll('a[href]')) {
        const path = productPath(link);
        const name = normalize(link.textContent);
        if (!path || !name) continue;
        if (!names.has(name)) names.set(name, new Set());
        names.get(name).add(path);
        const handle = path.split('/').pop();
        if (!handles.has(handle)) handles.set(handle, new Set());
        handles.get(handle).add(path);
      }
      for (const image of root.querySelectorAll('img')) {
        if (pending.has(image) || image.dataset.journalProduct) continue;
        // Older alt text can retain a typo also present in the product handle.
        const alt = normalize(image.alt);
        const matches = names.get(alt) || handles.get(alt.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
        const path = productPath(image.closest('a[href]')) || captionPath(image, root) || (matches?.size === 1 ? [...matches][0] : null);
        if (!path) continue; // Never guess from nearby editorial images or ambiguous titles.
        pending.add(image);
        jobs.push(async () => {
          try { await replaceImage(image, path); }
          catch (_) { /* Keep existing content if a product is removed or offline. */ }
          finally { pending.delete(image); }
        });
      }
    }
    // Bound simultaneous requests on long articles; repeated products share a request.
    await Promise.all(Array.from({ length: Math.min(4, jobs.length) }, async () => {
      while (jobs.length) await jobs.shift()();
    }));
  }

  window.cgJournalThumbnails = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
